import React, { useCallback, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useInfoActionSheet } from '@ocx-app/components/modals/InfoActionSheet';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { buttonTextMessages } from '@ocx-app/modules/localization/button-text.messages';
import { AchEnrollmentStatus } from '@ocx/graphql';

import { messages as spireEnrollmentModalMessages } from './spire-enrollment-modal.messages';
import { SpireEnrollmentModal } from './spire-enrollment-modal';
import { isEnrollmentCompleted, isEnrollmentIncomplete } from './spire-enrollment.utils';

export type SpireEnrollmentControllerProps = {
  returnPath: string;
  spireWebviewPath?: string;
  achEnrollmentStatus?: AchEnrollmentStatus | null;
};

export const SpireEnrollmentController = ({
  returnPath,
  spireWebviewPath,
  achEnrollmentStatus,
}: SpireEnrollmentControllerProps) => {
  const navigate = useNavigate();
  const { config } = useConfiguration();
  const { pushToWallet } = useGetNavigationActions();
  const { open: openInfoActionSheet } = useInfoActionSheet();
  const intl = useIntl();
  const { triggerEvent } = useRudderStack();

  const spireConfig = config.spireEnrollment;

  useEffect(() => {
    if (!spireConfig.enabled) {
      openInfoActionSheet({
        title: intl.formatMessage(spireEnrollmentModalMessages['spire-enrollment-modal:error-not-available']),
        actionButtonProps: {
          actionButtonText: intl.formatMessage(buttonTextMessages['button-text:close']),
          onActionButtonClick: () => {
            navigate(returnPath, { replace: true });
          },
        },
      });
    }
  }, [spireConfig.enabled, openInfoActionSheet, intl, navigate, returnPath]);

  const handleAddSpire = useCallback(() => {
    triggerEvent('spire_enrollment_add_spire_clicked');

    if (spireWebviewPath) {
      navigate(spireWebviewPath, { replace: true });
    } else {
      pushToWallet();
    }
  }, [navigate, spireWebviewPath, pushToWallet, triggerEvent]);

  const handleMaybeLater = useCallback(() => {
    triggerEvent('spire_enrollment_maybe_later_clicked');

    navigate(returnPath, { replace: true });
  }, [navigate, returnPath, triggerEvent]);

  if (!spireConfig.enabled) {
    return <Navigate to={returnPath} replace />;
  }

  if (isEnrollmentCompleted(achEnrollmentStatus)) {
    return <Navigate to={returnPath} replace />;
  }

  const isIncompleteEnrollment = isEnrollmentIncomplete(achEnrollmentStatus);

  return (
    <SpireEnrollmentModal
      onAddSpire={handleAddSpire}
      onMaybeLater={handleMaybeLater}
      isIncompleteEnrollment={isIncompleteEnrollment}
    />
  );
};
